import React, { useState, useEffect } from "react";
import styled from "styled-components";

const Step2 = ({
  step,
  setStep,
  errors,
  setErrors,
  formData,
  AgentTraits,
  handleInputChange,
  handleCheckboxChange,
}) => {
  const [showCustomPersonality, setShowCustomPersonality] = useState(false);
  const [customPersonalityInput, setCustomPersonalityInput] = useState("");

  useEffect(() => {
    const hasCustomPersonality = formData.personality.some(
      (h) =>
        !AgentTraits?.personalities?.some((hobby) => hobby?.titles?.title === h)
    );

    if (hasCustomPersonality) {
      setShowCustomPersonality(true);

      const customHobbies = formData.personality.filter(
        (h) =>
          !AgentTraits?.personalities?.some(
            (hobby) => hobby?.titles?.title === h
          )
      );

      setCustomPersonalityInput(customHobbies.join(", "));
    }
  }, [formData.personality, AgentTraits?.personalities]);

  const handleNext = (e) => {
    e.preventDefault();
    const stepErrors = validateStep2(formData);
    if (Object.keys(stepErrors).length === 0) {
      setStep(3);
    }
  };

  const validateStep2 = (data) => {
    const newErrors = {};
    if (!data.fullname) newErrors.fullname = "Full name is required";
    if (!data.dob) newErrors.dob = "Date of birth is required";
    if (!data.lifeStory) {
      newErrors.lifeStory = "Life story is required";
    } else if (data.lifeStory.length < 20) {
      newErrors.lifeStory = "Life story must be at least 20 characters";
    }
    if (!data.personality || data.personality.length === 0) {
      newErrors.personality = "At least one personality trait is required";
    }
    if (!data.education) newErrors.education = "Education is required";
    if (!data.profession) newErrors.profession = "Profession is required";
    setErrors(newErrors);
    return newErrors;
  };

  // Format date for display (YYYY-MM-DD to readable format)
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  // Get max date (today)
  const getMaxDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  // Get min date (100 years ago)
  const getMinDate = () => {
    const hundredYearsAgo = new Date();
    hundredYearsAgo.setFullYear(hundredYearsAgo.getFullYear() - 100);
    return hundredYearsAgo.toISOString().split("T")[0];
  };

  const handleOtherToggle = () => {
    const newShowCustom = !showCustomPersonality;
    setShowCustomPersonality(newShowCustom);

    // If unchecking "Other", remove all custom traits
    if (!newShowCustom) {
      setCustomPersonalityInput("");
      const predefinedTraits = formData.personality.filter((p) =>
        AgentTraits?.personalities?.some((trait) => trait?.titles?.title === p)
      );
      handleInputChange("personality", predefinedTraits);
    }
  };

  const handleCustomPersonalityChange = (e) => {
    const inputValue = e.target.value;
    setCustomPersonalityInput(inputValue);
  };

  const handleCustomPersonalityBlur = () => {
    // Parse comma-separated values
    const customTraits = customPersonalityInput
      .split(",")
      .map((trait) => trait.trim())
      .filter((trait) => trait.length > 0)
      // Filter out traits that already exist in predefined list (case-insensitive)
      .filter(
        (trait) =>
          !AgentTraits?.personalities?.some(
            (predefinedTrait) =>
              predefinedTrait?.titles?.title.toLowerCase() ===
              trait.toLowerCase()
          )
      );

    // Get predefined traits (from the checkboxes)
    const predefinedTraits = formData.personality.filter((p) =>
      AgentTraits?.personalities?.some((trait) => trait?.titles?.title === p)
    );

    // Update formData with combined traits
    handleInputChange("personality", [...predefinedTraits, ...customTraits]);

    // Update input to show only valid custom traits
    setCustomPersonalityInput(customTraits.join(", "));
  };

  return (
    <>
      <form className="Wrpper d-flex flex-column  gap-10">
        <div className="">
          <div className="head">
            <h5 className="m-0 fw-sbold text-[24px]">
              Fill out biography details
            </h5>
          </div>
          <div className="pb-5 pt-4 d-flex flex-column   gap-10 ">
            <div className="py-1">
              <label htmlFor="fullname" className="form-label m-0 pb-1 d-block">
                Full name
              </label>
              <div className="inputWrpper">
                <input
                  id="fullname"
                  placeholder="Type a name.........."
                  type="text"
                  value={formData.fullname}
                  onChange={(e) =>
                    handleInputChange("fullname", e.target.value)
                  }
                  className="form-control"
                />
                {errors.fullname && (
                  <div className="text-danger text-sm mt-1">
                    {errors.fullname}
                  </div>
                )}
              </div>
            </div>
            <div className="py-1">
              <label htmlFor="dob" className="form-label m-0 pb-1 d-block">
                Date of Birth
              </label>
              <div className="inputWrpper">
                <input
                  id="dob"
                  placeholder="Select date of birth"
                  type="date"
                  value={formData.dob}
                  onChange={(e) => handleInputChange("dob", e.target.value)}
                  className="form-control"
                  max={getMaxDate()}
                  // min={getMinDate()}
                />
                {errors.dob && (
                  <div className="text-danger text-sm mt-1">{errors.dob}</div>
                )}
              </div>
            </div>
            <div className="py-1">
              <label
                htmlFor="lifeStory"
                className="form-label m-0 pb-1 d-block"
              >
                Enter Life Story
              </label>
              <div className="inputWrpper">
                <textarea
                  id="lifeStory"
                  name="lifeStory"
                  placeholder="Enter Life Story"
                  rows={7}
                  value={formData.lifeStory}
                  onChange={(e) =>
                    handleInputChange("lifeStory", e.target.value)
                  }
                  className="form-control"
                />
                <div className="text-xs text-muted mt-1">
                  {formData.lifeStory ? formData.lifeStory.length : 0}{" "}
                  characters (minimum 20 required)
                </div>
                {errors.lifeStory && (
                  <div className="text-danger text-sm mt-1">
                    {errors.lifeStory}
                  </div>
                )}
              </div>
            </div>
            <div className="py-1">
              <label
                htmlFor="personality"
                className="form-label m-0 pb-1 d-block"
              >
                Select personality Traits from Listing
              </label>
              <RadioList className="list-unstyled ps-0 mb-0 d-flex gap-10 flex-wrap mt-3">
                {AgentTraits?.personalities?.map((val, index) => {
                  return (
                    <li key={index} className="position-relative">
                      <input
                        type="checkbox"
                        id={`personality-${index}`}
                        name={val?.titles?.title}
                        checked={formData.personality.includes(
                          val?.titles?.title
                        )}
                        onChange={() =>
                          handleCheckboxChange(
                            "personality",
                            val?.titles?.title
                          )
                        }
                        className="position-absolute peer top-0 left-0 h-100 w-100 z-[99] cursor-pointer opacity-0"
                      />
                      <label
                        htmlFor={`personality-${index}`}
                        className="h-[55px] peer-checked:bg-[#4A2CE5] peer-checked:text-white peer-checked:border-[#4A2CE5] d-flex align-items-center justify-content-center rounded-pill px-3 py-2 min-w-[162px] border border-[#868686]/30 fw-sbold cursor-pointer"
                      >
                        {val?.titles?.title}
                      </label>
                    </li>
                  );
                })}
                <li className="position-relative">
                  <input
                    type="checkbox"
                    id="personality-other"
                    name="Other"
                    checked={showCustomPersonality}
                    onChange={handleOtherToggle}
                    className="position-absolute peer top-0 left-0 h-100 w-100 z-[99] cursor-pointer opacity-0"
                  />
                  <label
                    htmlFor="personality-other"
                    className="h-[55px] peer-checked:bg-[#4A2CE5] peer-checked:text-white peer-checked:border-[#4A2CE5] d-flex align-items-center justify-content-center rounded-pill px-3 py-2 min-w-[162px] border border-[#868686]/30 fw-sbold cursor-pointer"
                  >
                    Other
                  </label>
                </li>
              </RadioList>
              {errors.personality && (
                <div className="text-danger text-sm mt-1">
                  {errors.personality}
                </div>
              )}
              {showCustomPersonality && (
                <div className="py-2">
                  <label
                    htmlFor="custom-personality"
                    className="form-label m-0 pb-1 d-block"
                  >
                    Enter Custom Personality Traits
                  </label>
                  <input
                    type="text"
                    id="custom-personality"
                    placeholder="e.g., Creative, Adventurous, Analytical"
                    className="form-control"
                    value={customPersonalityInput}
                    onChange={handleCustomPersonalityChange}
                    onBlur={handleCustomPersonalityBlur}
                  />
                  <div className="text-[13px] text-gray-500 mt-1">
                    Note: Traits already in the list above will be automatically
                    excluded
                  </div>
                </div>
              )}

              {errors.personality && (
                <div className="text-red-500 text-sm mt-1">
                  {errors.personality}
                </div>
              )}
            </div>
            <div className="py-1">
              <label
                htmlFor="education"
                className="form-label m-0 pb-1 d-block"
              >
                Enter Education
              </label>
              <div className="inputWrpper">
                <input
                  id="education"
                  value={formData.education}
                  onChange={(e) =>
                    handleInputChange("education", e.target.value)
                  }
                  placeholder="Enter Education"
                  type="text"
                  className="form-control"
                />
                {errors.education && (
                  <div className="text-danger text-sm mt-1">
                    {errors.education}
                  </div>
                )}
              </div>
            </div>
            <div className="py-1">
              <label
                htmlFor="profession"
                className="form-label m-0 pb-1 d-block"
              >
                Enter profession
              </label>
              <div className="inputWrpper">
                <input
                  id="profession"
                  placeholder="Enter profession"
                  type="text"
                  value={formData.profession}
                  onChange={(e) =>
                    handleInputChange("profession", e.target.value)
                  }
                  className="form-control"
                />
                {errors.profession && (
                  <div className="text-danger text-sm mt-1">
                    {errors.profession}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="mt-10 mx-auto w-100" style={{maxWidth: 300}}>
          <button
            onClick={handleNext}
            className="d-flex align-items-center  justify-content-center  bg-blue-600 hover:bg-blue-700 text-white h-[40px] commonBtn btn rounded-pill
             max-w-[377px] mx-auto transition-colors w-100"
            type="submit"
          >
            Next
          </button>
          <button
            onClick={() => setStep(1)}
            className="d-flex align-items-center  justify-content-center  bg-blue-600 hover:bg-blue-700 text-white h-[40px] commonBtn btn rounded-pill
             max-w-[377px] mx-auto transition-colors gap -2 w-100 mt-3"
            type="button"
          >
            Back
          </button>
        </div>
      </form>
    </>
  );
};

const RadioList = styled.ul`
  li {
    input {
      cursor: pointer;
      z-index: 99;
    }
    input:checked + label {
      border-color: #9701ea !important;
      background: #9701ea;
      color: #fff;
    }
    label:hover {
      border-color: #9701ea;
      background: rgba(151, 1, 234, 0.1);
    }
  }
`;

export default Step2;

const fileIcn = (
  <svg
    width="44"
    height="44"
    viewBox="0 0 44 44"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="44" height="44" rx="22" fill="#4A2CE5" />
    <path
      d="M18.9995 27.7505C18.5895 27.7505 18.2495 27.4105 18.2495 27.0005V22.8105L17.5295 23.5305C17.2395 23.8205 16.7595 23.8205 16.4695 23.5305C16.1795 23.2405 16.1795 22.7605 16.4695 22.4705L18.4695 20.4705C18.6795 20.2605 19.0095 20.1905 19.2895 20.3105C19.5695 20.4205 19.7495 20.7005 19.7495 21.0005V27.0005C19.7495 27.4105 19.4095 27.7505 18.9995 27.7505Z"
      fill="white"
    />
    <path
      d="M20.9995 23.7495C20.8095 23.7495 20.6195 23.6795 20.4695 23.5295L18.4695 21.5295C18.1795 21.2395 18.1795 20.7595 18.4695 20.4695C18.7595 20.1795 19.2395 20.1795 19.5295 20.4695L21.5295 22.4695C21.8195 22.7595 21.8195 23.2395 21.5295 23.5295C21.3795 23.6795 21.1895 23.7495 20.9995 23.7495Z"
      fill="white"
    />
    <path
      d="M25 32.75H19C13.57 32.75 11.25 30.43 11.25 25V19C11.25 13.57 13.57 11.25 19 11.25H24C24.41 11.25 24.75 11.59 24.75 12C24.75 12.41 24.41 12.75 24 12.75H19C14.39 12.75 12.75 14.39 12.75 19V25C12.75 29.61 14.39 31.25 19 31.25H25C29.61 31.25 31.25 29.61 31.25 25V20C31.25 19.59 31.59 19.25 32 19.25C32.41 19.25 32.75 19.59 32.75 20V25C32.75 30.43 30.43 32.75 25 32.75Z"
      fill="white"
    />
    <path
      d="M32 20.7505H28C24.58 20.7505 23.25 19.4205 23.25 16.0005V12.0005C23.25 11.7005 23.43 11.4205 23.71 11.3105C23.99 11.1905 24.31 11.2605 24.53 11.4705L32.53 19.4705C32.74 19.6805 32.81 20.0105 32.69 20.2905C32.57 20.5705 32.3 20.7505 32 20.7505ZM24.75 13.8105V16.0005C24.75 18.5805 25.42 19.2505 28 19.2505H30.19L24.75 13.8105Z"
      fill="white"
    />
  </svg>
);
